import React from 'react';
import {
  IconMenu,
  IconMenuOpen,
  IconArrowDown,
  IconArrowUp,
} from '@bynder/icons';
import { Button, Tooltip } from '@bynder/design-system';
import { StyledButtonBackground } from './styled-components/ButtonBackground';
import useDeviceDetect from '../../hooks/useDeviceDetect';
import { useGuideStore } from '../../stores/guideStore';
import { useSidebarStore } from '../../stores/sidebarStore';
import {
  StyledButtonContainer,
  StyledNavigation,
  StyledNavigationList,
} from './styled-components/Sidebar';

interface Props {
  children?: React.ReactNode;
}

const Navigation: React.FC<Props> = ({ children }) => {
  const { isMobile } = useDeviceDetect();
  const isEditMode = useGuideStore(s => s.isEditMode);
  const isSidebarCollapsed = useSidebarStore(s => s.isSidebarCollapsed);
  const toggleIsSidebarCollaped = useSidebarStore(
    s => s.toggleIsSidebarCollaped,
  );

  const areSidebarItemsCollapsed = useSidebarStore(
    s => s.areSidebarItemsCollapsed,
  );
  const toggleAreSidebarItemsCollapsed = useSidebarStore(
    s => s.toggleAreSidebarItemsCollapsed,
  );

  const [isHovering, setIsHovering] = React.useState(false);

  React.useEffect(() => {
    if (isMobile) {
      toggleIsSidebarCollaped(true);
      window.localStorage.setItem('sidebar-is-collapsed', String(true));
    }
  }, [isMobile, toggleIsSidebarCollaped]);

  const handleCollapse = () => {
    toggleIsSidebarCollaped();
    window.localStorage.setItem(
      'sidebar-is-collapsed',
      String(!isSidebarCollapsed),
    );
  };

  const handleCollapseSidebarItems = () => {
    toggleAreSidebarItemsCollapsed();
    window.localStorage.setItem(
      'sidebar-items-are-collapsed',
      String(!areSidebarItemsCollapsed),
    );
  };

  return (
    <>
      <StyledButtonContainer $isCollapsed={isSidebarCollapsed}>
        <StyledButtonBackground>
          <Button
            data-testid="toggle-sidebar-button"
            isSmall
            variant="clean"
            onClick={handleCollapse}
            aria-label="collapse"
            icon={isSidebarCollapsed ? <IconMenu /> : <IconMenuOpen />}
          />
        </StyledButtonBackground>

        {isHovering && !isSidebarCollapsed && !isEditMode && (
          <Tooltip
            timing="delay"
            content={areSidebarItemsCollapsed ? 'Expand all' : 'Collapse all'}
          >
            <StyledButtonBackground>
              <Button
                isSmall
                variant="clean"
                onClick={handleCollapseSidebarItems}
                aria-label="collapse"
                icon={
                  areSidebarItemsCollapsed ? <IconArrowDown /> : <IconArrowUp />
                }
              />
            </StyledButtonBackground>
          </Tooltip>
        )}
      </StyledButtonContainer>
      <StyledNavigation
        $isCollapsed={isSidebarCollapsed}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <StyledNavigationList $isCollapsed={isSidebarCollapsed}>
          {children}
        </StyledNavigationList>
      </StyledNavigation>
    </>
  );
};

export default Navigation;
